import React, { useState } from "react";
import { 
  Play, 
  Pause, 
  Square, 
  Plus, 
  Minus, 
  Shield, 
  ChevronDown, 
  ChevronUp,
  AlertTriangle,
  Calendar,
  Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAdminRole } from "@/hooks/useAdminRole";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface ExhibitionAdminControlsProps {
  eventId: string | null;
  eventState: string;
  isAdminOverride: boolean;
  isPaused?: boolean;
  debugInfo: Record<string, unknown> | null;
  onStateChange: () => void;
}

const ExhibitionAdminControls: React.FC<ExhibitionAdminControlsProps> = ({
  eventId,
  eventState,
  isAdminOverride,
  isPaused = false,
  debugInfo,
  onStateChange,
}) => {
  const { isAdminOrModerator } = useAdminRole();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newEventName, setNewEventName] = useState("Love Exhibition");
  const [actionReason, setActionReason] = useState("");

  if (!isAdminOrModerator) return null;

  // Force Live toggle (Open = Force Live ON)
  const handleToggleAdminOverride = async (enabled: boolean) => {
    if (!eventId) {
      toast.error("No event to modify");
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.rpc("set_exhibition_admin_override", {
        p_event_id: eventId,
        p_enabled: enabled,
        p_reason: actionReason || `Admin override ${enabled ? "enabled" : "disabled"}`,
      });

      if (error) throw error;

      toast.success(enabled ? "Event is now LIVE" : "Event returned to schedule");
      setActionReason("");
      onStateChange();
    } catch (error) {
      console.error("Error toggling admin override:", error);
      toast.error("Failed to toggle admin override");
    } finally {
      setIsLoading(false);
    }
  };

  // Pause toggle
  const handleTogglePause = async (paused: boolean) => {
    if (!eventId) {
      toast.error("No event to modify");
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.rpc("set_exhibition_paused", {
        p_event_id: eventId,
        p_paused: paused,
        p_reason: actionReason || `Event ${paused ? "paused" : "unpaused"}`,
      });

      if (error) throw error;

      toast.success(paused ? "Event paused" : "Event resumed");
      setActionReason("");
      onStateChange();
    } catch (error) {
      console.error("Error toggling pause:", error);
      toast.error("Failed to toggle pause");
    } finally {
      setIsLoading(false);
    }
  };

  // Close event now
  const handleCloseEvent = async () => {
    if (!eventId) {
      toast.error("No event to close");
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.rpc("close_exhibition_event", {
        p_event_id: eventId,
        p_reason: actionReason || "Event closed by admin",
      });

      if (error) throw error;

      toast.success("Event closed");
      setActionReason("");
      onStateChange();
    } catch (error) {
      console.error("Error closing event:", error);
      toast.error("Failed to close event");
    } finally {
      setIsLoading(false);
    }
  };

  // Adjust end time
  const handleAdjustTime = async (minutes: number) => {
    if (!eventId) {
      toast.error("No event to modify");
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.rpc("adjust_exhibition_end_time", {
        p_event_id: eventId,
        p_minutes_delta: minutes,
        p_reason: actionReason || `Time adjusted by ${minutes} minutes`,
      });

      if (error) throw error;

      toast.success(`Event ${minutes > 0 ? "extended" : "reduced"} by ${Math.abs(minutes)} minutes`);
      setActionReason("");
      onStateChange();
    } catch (error) {
      console.error("Error adjusting time:", error);
      toast.error("Failed to adjust time");
    } finally {
      setIsLoading(false);
    }
  };

  // Create new event
  const handleCreateEvent = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase.rpc("create_exhibition_event", {
        p_name: newEventName,
        p_description: "Weekly Love Exhibition event",
      });

      if (error) throw error;

      toast.success("Event created successfully");
      setShowCreateDialog(false);
      setNewEventName("Love Exhibition");
      onStateChange();
    } catch (error) {
      console.error("Error creating event:", error);
      toast.error("Failed to create event");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed bottom-20 right-4 z-50">
      {/* Admin Preview Mode Badge */}
      {isAdminOverride && (
        <div className="absolute -top-8 right-0 px-3 py-1 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center gap-1 animate-pulse">
          <Zap className="w-3 h-3" />
          Admin Preview Mode
        </div>
      )}

      {/* Collapsed button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "flex items-center gap-2 px-4 py-3 rounded-2xl shadow-lg transition-all",
          "bg-gradient-to-r from-violet-600 to-purple-600 text-white",
          "hover:shadow-xl hover:scale-105"
        )}
      >
        <Shield className="w-5 h-5" />
        <span className="font-semibold">Admin</span>
        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
      </button>

      {/* Expanded panel */}
      {isExpanded && (
        <div className="absolute bottom-14 right-0 w-80 max-h-[70vh] overflow-y-auto p-4 rounded-2xl bg-white dark:bg-gray-900 shadow-2xl border border-border">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-foreground">Exhibition Controls</h3>
            <span className={cn(
              "px-2 py-1 rounded-full text-xs font-semibold",
              eventState === "live" && "bg-green-500/20 text-green-600",
              eventState === "locked" && "bg-amber-500/20 text-amber-600",
              eventState === "closed" && "bg-red-500/20 text-red-600",
              eventState === "no-event" && "bg-gray-500/20 text-gray-600"
            )}>
              {eventState.toUpperCase()}
            </span>
          </div>

          {/* No event - Create button */}
          {(!eventId || eventState === "no-event") && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">No active event found</p>
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogTrigger asChild>
                  <Button className="w-full gap-2" variant="default">
                    <Calendar className="w-4 h-4" />
                    Create Event
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create Exhibition Event</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <label className="text-sm font-medium text-foreground">Event Name</label>
                      <Input
                        value={newEventName}
                        onChange={(e) => setNewEventName(e.target.value)}
                        placeholder="Love Exhibition"
                        className="mt-1"
                      />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Default schedule: Next Saturday 12:00 PM - 11:00 PM EST
                    </p>
                    <Button
                      onClick={handleCreateEvent}
                      disabled={isLoading || !newEventName}
                      className="w-full"
                    >
                      Create Event
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* Event controls */}
          {eventId && eventState !== "no-event" && (
            <div className="space-y-4">
              {/* Admin Override Toggle (Force Live) */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase">Admin Override</label>
                <div 
                  className={cn(
                    "flex items-center justify-between p-3 rounded-lg border transition-colors",
                    isAdminOverride 
                      ? "bg-amber-500/10 border-amber-500/30" 
                      : "bg-muted/50 border-border"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <Zap className={cn(
                      "w-4 h-4",
                      isAdminOverride ? "text-amber-500" : "text-muted-foreground"
                    )} />
                    <div>
                      <p className="text-sm font-medium">Force Live</p>
                      <p className="text-xs text-muted-foreground">
                        {isAdminOverride ? "Event is live regardless of schedule" : "Follow scheduled times"}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={isAdminOverride}
                    onCheckedChange={handleToggleAdminOverride}
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* State controls */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase">State Control</label>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={eventState === "live" && !isPaused ? "default" : "outline"}
                    onClick={() => handleToggleAdminOverride(true)}
                    disabled={isLoading || (eventState === "live" && isAdminOverride)}
                    className="flex-1 gap-1"
                  >
                    <Play className="w-3 h-3" />
                    Open
                  </Button>
                  <Button
                    size="sm"
                    variant={isPaused ? "default" : "outline"}
                    onClick={() => handleTogglePause(!isPaused)}
                    disabled={isLoading}
                    className="flex-1 gap-1"
                  >
                    <Pause className="w-3 h-3" />
                    {isPaused ? "Resume" : "Pause"}
                  </Button>
                  <Button
                    size="sm"
                    variant={eventState === "closed" ? "destructive" : "outline"}
                    onClick={handleCloseEvent}
                    disabled={isLoading || eventState === "closed"}
                    className="flex-1 gap-1"
                  >
                    <Square className="w-3 h-3" />
                    Close
                  </Button>
                </div>
              </div>

              {/* Time adjustments */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase">Adjust Time</label>
                <div className="grid grid-cols-4 gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleAdjustTime(-30)}
                    disabled={isLoading}
                    className="text-xs"
                  >
                    <Minus className="w-3 h-3 mr-1" />30m
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleAdjustTime(-15)}
                    disabled={isLoading}
                    className="text-xs"
                  >
                    <Minus className="w-3 h-3 mr-1" />15m
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleAdjustTime(15)}
                    disabled={isLoading}
                    className="text-xs"
                  >
                    <Plus className="w-3 h-3 mr-1" />15m
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleAdjustTime(30)}
                    disabled={isLoading}
                    className="text-xs"
                  >
                    <Plus className="w-3 h-3 mr-1" />30m
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleAdjustTime(60)}
                    disabled={isLoading}
                    className="text-xs"
                  >
                    <Plus className="w-3 h-3 mr-1" />1 hour
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleAdjustTime(-60)}
                    disabled={isLoading}
                    className="text-xs"
                  >
                    <Minus className="w-3 h-3 mr-1" />1 hour
                  </Button>
                </div>
              </div>

              {/* Action reason */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase">Reason (optional)</label>
                <Input
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder="Reason for action..."
                  className="text-sm"
                />
              </div>

              {/* Debug info (collapsible) */}
              {debugInfo && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                    Debug Info
                  </summary>
                  <pre className="mt-2 p-2 rounded bg-muted text-xs overflow-auto max-h-32">
                    {JSON.stringify(debugInfo, null, 2)}
                  </pre>
                </details>
              )}

              {/* Admin override warning */}
              {isAdminOverride && (
                <div className="flex items-start gap-2 p-2 rounded-lg bg-amber-500/10 text-amber-600 text-xs">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Admin override active. Event is shown as LIVE regardless of schedule.</span>
                </div>
              )}

              {/* Paused warning */}
              {isPaused && (
                <div className="flex items-start gap-2 p-2 rounded-lg bg-blue-500/10 text-blue-600 text-xs">
                  <Pause className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Event is paused. Users see the locked state.</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExhibitionAdminControls;
